// Soft water-chime synthesizer using Web Audio API.
// Plus a low ocean-wave ambient synthesized for the app intro.
// No external asset needed.

let ctx = null;
let unlocked = false;
let oceanNode = null; // active ocean-wave source so we can stop it gracefully

function getCtx() {
  if (ctx) return ctx;
  // Wait for first user gesture — creating AudioContext before that triggers
  // the Chrome autoplay-policy warning and the context starts "suspended"
  // anyway. Returning null silently is the cleanest behaviour.
  if (!unlocked) return null;
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    ctx = new AC();
  } catch {
    ctx = null;
  }
  return ctx;
}

// Browsers block audio until the user has interacted. We register
// a one-time listener that resumes the AudioContext on the first gesture,
// so subsequent calls to playChime() (even programmatic ones) will be audible.
function registerUnlock() {
  if (typeof window === "undefined" || unlocked) return;
  const unlock = () => {
    unlocked = true;
    const ac = getCtx();
    if (ac && ac.state === "suspended") { try { ac.resume(); } catch { /* ignore */ } }
    window.removeEventListener("pointerdown", unlock);
    window.removeEventListener("keydown", unlock);
    window.removeEventListener("touchstart", unlock);
  };
  window.addEventListener("pointerdown", unlock, { once: true });
  window.addEventListener("keydown", unlock, { once: true });
  window.addEventListener("touchstart", unlock, { once: true });
}

if (typeof window !== "undefined") registerUnlock();

/**
 * Play a short "water chime" (≈450ms) — used for UI feedback.
 * Silent fallback if AudioContext unsupported or still locked by autoplay policy.
 */
export function playChime({ volume = 0.09 } = {}) {
  const ac = getCtx();
  if (!ac) return;
  if (ac.state === "suspended") { try { ac.resume(); } catch { /* ignore */ } }

  const t0 = ac.currentTime;
  const master = ac.createGain();
  master.gain.setValueAtTime(volume, t0);
  master.connect(ac.destination);

  // Fundamental: A5 (880Hz) with quick exponential decay
  const partials = [
    { freq: 880,  dur: 0.45, gain: 1.0 },
    { freq: 1318, dur: 0.35, gain: 0.55 }, // perfect fifth above (E6)
    { freq: 2637, dur: 0.22, gain: 0.22 }, // shimmer octave
  ];

  partials.forEach(({ freq, dur, gain }) => {
    const osc = ac.createOscillator();
    osc.type = "sine";
    osc.frequency.setValueAtTime(freq, t0);
    // tiny downward glide for "water drop" character
    osc.frequency.exponentialRampToValueAtTime(freq * 0.985, t0 + dur);

    const g = ac.createGain();
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(gain, t0 + 0.012);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);

    osc.connect(g).connect(master);
    osc.start(t0);
    osc.stop(t0 + dur + 0.05);
  });
}


/**
 * Play a subtle ocean-wave ambient loop. Used during the splash intro
 * to evoke Madeira's Atlantic coastline.
 *
 * Synthesis: brown-noise buffer → low-pass filter (~800Hz) modulated by a
 * very slow LFO (0.15Hz) to simulate the rise-and-fall of a wave.
 *
 * Call `stopOcean()` to fade out smoothly.
 */
export function playOcean({ volume = 0.025 } = {}) {
  const ac = getCtx();
  if (!ac) return false;
  if (ac.state === "suspended") { try { ac.resume(); } catch { /* ignore */ } }
  if (oceanNode) return true; // already playing

  const t0 = ac.currentTime;
  // 4-second brown noise buffer, looped
  const len = ac.sampleRate * 4;
  const buf = ac.createBuffer(1, len, ac.sampleRate);
  const data = buf.getChannelData(0);
  let last = 0;
  for (let i = 0; i < len; i++) {
    const white = Math.random() * 2 - 1;
    last = (last + 0.02 * white) / 1.02;
    data[i] = last * 3.5;
  }

  const noise = ac.createBufferSource();
  noise.buffer = buf;
  noise.loop = true;

  const lp = ac.createBiquadFilter();
  lp.type = "lowpass";
  lp.frequency.setValueAtTime(600, t0);
  lp.Q.setValueAtTime(0.7, t0);

  // Slow LFO for wave swell on the filter cutoff
  const lfo = ac.createOscillator();
  lfo.type = "sine";
  lfo.frequency.setValueAtTime(0.15, t0);
  const lfoGain = ac.createGain();
  lfoGain.gain.setValueAtTime(350, t0);
  lfo.connect(lfoGain).connect(lp.frequency);

  // Master gain with fade-in
  const master = ac.createGain();
  master.gain.setValueAtTime(0.0001, t0);
  master.gain.exponentialRampToValueAtTime(volume, t0 + 3.5);

  noise.connect(lp).connect(master).connect(ac.destination);
  noise.start(t0);
  lfo.start(t0);

  oceanNode = { noise, lfo, master };
  return true;
}

/**
 * Smoothly ramp the ocean's gain to a new value (or restore it). Used by
 * the radio player to "duck" the waves under the voice slogan.
 */
export function setOceanVolume(volume = 0.025, fadeMs = 350) {
  if (!oceanNode) return;
  const ac = getCtx();
  if (!ac) return;
  const t = ac.currentTime;
  const { master } = oceanNode;
  try {
    master.gain.cancelScheduledValues(t);
    master.gain.setValueAtTime(master.gain.value || 0.0001, t);
    master.gain.exponentialRampToValueAtTime(Math.max(0.0001, volume), t + fadeMs / 1000);
  } catch { /* ignore */ }
}

export function stopOcean({ fadeMs = 800 } = {}) {
  if (!oceanNode) return;
  const ac = getCtx();
  if (!ac) return;
  const t = ac.currentTime;
  const { noise, lfo, master } = oceanNode;
  try {
    master.gain.cancelScheduledValues(t);
    master.gain.setValueAtTime(master.gain.value || 0.08, t);
    master.gain.exponentialRampToValueAtTime(0.0001, t + fadeMs / 1000);
  } catch { /* ignore */ }
  setTimeout(() => {
    try { noise.stop(); } catch { /* ignore */ }
    try { lfo.stop(); } catch { /* ignore */ }
  }, fadeMs + 50);
  oceanNode = null;
}

// ---------------------------------------------------------------------------
// AMBIENT BACKGROUND MUSIC
// ---------------------------------------------------------------------------
// Soft, slow piano-like arpeggios layered over a warm string pad. Generates a
// meditative lounge-lobby vibe that suits the "luxury accommodation" theme.
// Uses a tiny scheduler that queues bars ahead of time (look-ahead pattern).
// ---------------------------------------------------------------------------

let ambientNode = null; // { padOsc1, padOsc2, master, timer, stopped }

// Chord progression in a warm, cinematic key of D minor (natural minor).
// Each chord = array of semitone offsets from the root D3 (MIDI 50).
// Rotates every 4 bars.
const D3 = 146.83; // Hz — D3
const SCALE_MINOR = [0, 3, 5, 7, 10, 12, 15, 17, 19]; // for arpeggio picks
const CHORD_PROGRESSION = [
  { root: 0, tones: [0, 7, 12, 15] },   // Dm  (i)
  { root: -2, tones: [0, 7, 12, 16] },  // C/E (VII)
  { root: 3, tones: [0, 7, 12, 15] },   // Bb  (VI)
  { root: 5, tones: [0, 7, 12, 16] },   // A   (V→i)
];

function hzFromSemitones(baseHz, semi) {
  return baseHz * Math.pow(2, semi / 12);
}

function piano(ac, freq, t0, duration, volume) {
  // Simple 2-osc piano: fundamental + one octave + short attack, exponential decay
  const osc1 = ac.createOscillator();
  osc1.type = "triangle";
  osc1.frequency.value = freq;
  const osc2 = ac.createOscillator();
  osc2.type = "sine";
  osc2.frequency.value = freq * 2;

  const gain = ac.createGain();
  gain.gain.setValueAtTime(0.0001, t0);
  gain.gain.exponentialRampToValueAtTime(volume, t0 + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);

  // Light lowpass to soften the brightness
  const lp = ac.createBiquadFilter();
  lp.type = "lowpass";
  lp.frequency.value = 2200;
  lp.Q.value = 0.3;

  osc1.connect(gain);
  osc2.connect(gain);
  gain.connect(lp);
  osc1.start(t0); osc2.start(t0);
  osc1.stop(t0 + duration + 0.05);
  osc2.stop(t0 + duration + 0.05);
  return lp;
}

export function playAmbientMusic({ volume = 0.025 } = {}) {
  const ac = getCtx();
  if (!ac) return false;
  if (ac.state === "suspended") { try { ac.resume(); } catch { /* ignore */ } }
  if (ambientNode) return true; // already playing

  const t0 = ac.currentTime + 0.1;

  // Ultra-subtle drone: two sawtooths deep in the bass filtered heavy, with a
  // very slow LFO wobble. NO arpeggios / piano notes — just a warm continuous
  // pad so the UX feels "spa-like" instead of musical.
  const padOsc1 = ac.createOscillator();
  padOsc1.type = "sawtooth";
  padOsc1.frequency.value = D3 * 0.5; // one octave DOWN for calmer, deeper feel
  const padOsc2 = ac.createOscillator();
  padOsc2.type = "sawtooth";
  padOsc2.frequency.value = D3 * 0.5 * Math.pow(2, 7 / 12) * 1.004; // perfect 5th

  const padFilter = ac.createBiquadFilter();
  padFilter.type = "lowpass";
  padFilter.frequency.value = 400; // heavier muffling
  padFilter.Q.value = 0.6;

  // Very slow filter wobble (5 sec cycle) for subtle breathing
  const padLfo = ac.createOscillator();
  padLfo.type = "sine";
  padLfo.frequency.value = 0.05; // 20 sec period
  const padLfoGain = ac.createGain();
  padLfoGain.gain.value = 60;
  padLfo.connect(padLfoGain).connect(padFilter.frequency);

  const padGain = ac.createGain();
  padGain.gain.setValueAtTime(0.0001, t0);
  padGain.gain.exponentialRampToValueAtTime(volume, t0 + 6); // slow 6s fade-in

  padOsc1.connect(padFilter);
  padOsc2.connect(padFilter);
  padFilter.connect(padGain);

  const master = ac.createGain();
  master.gain.value = 1.0;
  padGain.connect(master);
  master.connect(ac.destination);

  padOsc1.start(t0);
  padOsc2.start(t0);
  padLfo.start(t0);

  const BAR = 0; // no arpeggio scheduler needed
  let bar = 0;
  let stopped = false;

  // Stub scheduleBar — kept as no-op so the interval below never errors if ever
  // re-enabled. The pad alone carries the ambient layer.
  const scheduleBar = () => {};

  // Kick off the ambient drone — no arpeggio scheduling.
  void bar; void scheduleBar; // explicit discard (kept as stubs for future use)

  const timer = null; // no scheduler — pure drone

  ambientNode = { padOsc1, padOsc2, padLfo, padGain, master, timer, stopped: false };
  return true;
}

export function stopAmbientMusic({ fadeMs = 1200 } = {}) {
  if (!ambientNode) return;
  const ac = getCtx();
  if (!ac) return;
  ambientNode.stopped = true;
  if (ambientNode.timer) clearInterval(ambientNode.timer);
  const t = ac.currentTime;
  const { padOsc1, padOsc2, padLfo, master } = ambientNode;
  try {
    master.gain.cancelScheduledValues(t);
    master.gain.setValueAtTime(master.gain.value || 0.1, t);
    master.gain.exponentialRampToValueAtTime(0.0001, t + fadeMs / 1000);
  } catch { /* ignore */ }
  setTimeout(() => {
    try { padOsc1.stop(); } catch { /* ignore */ }
    try { padOsc2.stop(); } catch { /* ignore */ }
    try { padLfo.stop(); } catch { /* ignore */ }
  }, fadeMs + 60);
  ambientNode = null;
}

export function isAmbientPlaying() {
  return !!ambientNode;
}
